import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import { getClientes, getTrabajadores, postCitaCalendario } from '../../services/apis';
import { ordenarOpcionesPorLabel, textoMayusculas } from '../../utils/utils';
import { etiquetaDiaCompleto, parsearIdDia } from './calendarioUtils';

const formatFechaSql = (idDia, hora = '') => {
    const fecha = parsearIdDia(idDia);
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    const horaNormalizada = hora && /^\d{2}:\d{2}$/.test(hora) ? hora : '';

    return horaNormalizada
        ? `${year}-${month}-${day} ${horaNormalizada}:00`
        : `${year}-${month}-${day}`;
};

export const CrearCitaCalendarioDialog = ({ open, fecha, onClose, onCreated }) => {
    const [asunto, setAsunto] = useState('');
    const [hora, setHora] = useState('');
    const [opClientes, setOpClientes] = useState([]);
    const [cliente, setCliente] = useState(null);
    const [opTrabajadores, setOpTrabajadores] = useState([]);
    const [trabajadores, setTrabajadores] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            setAsunto('');
            setHora('');
            setCliente(null);
            setTrabajadores([]);
            setSaving(false);
        }
    }, [open]);

    useEffect(() => {
        const cargarClientes = async () => {
            if (!open || opClientes.length) return;

            const response = await getClientes();
            const opciones = ordenarOpcionesPorLabel((response?.data || []).map((item) => ({
                label: textoMayusculas(item.nombre),
                value: item.id,
            })));

            setOpClientes(opciones);
        };

        cargarClientes();
    }, [open, opClientes.length]);

    useEffect(() => {
        const cargarTrabajadores = async () => {
            if (!open || opTrabajadores.length) return;

            const response = await getTrabajadores();
            const opciones = ordenarOpcionesPorLabel((response?.data || []).map((item) => ({
                label: textoMayusculas(item.nombre),
                value: item.id,
            })));

            setOpTrabajadores(opciones);
        };

        cargarTrabajadores();
    }, [open, opTrabajadores.length]);

    const handleGuardar = async () => {
        if (!asunto.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Falta el asunto',
                text: 'Escribe un asunto para apuntarlo en el calendario.',
            });
            return;
        }

        setSaving(true);
        const trabajadoresAsignados = trabajadores
            .map((trabajador) => trabajador?.label)
            .filter(Boolean)
            .join(', ');
        const descripcion = [
            cliente?.label ? `CLIENTE_TAREA:${cliente.label}` : '',
            trabajadoresAsignados ? `TRABAJADORES_TAREA:${trabajadoresAsignados}` : '',
        ].filter(Boolean).join('\n');

        const response = await postCitaCalendario({
            asunto: asunto.trim(),
            fecha: formatFechaSql(fecha, hora),
            descripcion,
        });
        setSaving(false);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo guardar',
                text: response?.message || 'Revisa los datos e intentalo de nuevo.',
            });
            return;
        }

        if (onCreated) {
            await onCreated(response.data);
        }

        onClose();
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>
                Apuntar en calendario
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Typography sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                        {fecha ? etiquetaDiaCompleto(fecha) : ''}
                    </Typography>

                    <TextField
                        label="Asunto"
                        value={asunto}
                        autoFocus
                        fullWidth
                        onChange={(event) => setAsunto(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleGuardar();
                            }
                        }}
                    />

                    <TextField
                        label="Hora"
                        type="time"
                        value={hora}
                        fullWidth
                        onChange={(event) => setHora(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText="Opcional"
                    />

                    <Autocomplete
                        options={opClientes}
                        value={cliente}
                        onChange={(event, value) => setCliente(value)}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) =>
                            String(option.value) === String(value.value)
                                || String(option.label || '').toLocaleLowerCase('es-ES') === String(value.label || '').toLocaleLowerCase('es-ES')
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Cliente"
                                helperText="Opcional"
                            />
                        )}
                    />

                    <Autocomplete
                        multiple
                        options={opTrabajadores}
                        value={trabajadores}
                        onChange={(event, value) => setTrabajadores(value)}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) =>
                            String(option.value) === String(value.value)
                                || String(option.label || '').toLocaleLowerCase('es-ES') === String(value.label || '').toLocaleLowerCase('es-ES')
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Trabajadores"
                                helperText="Opcional"
                            />
                        )}
                    />
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.6 }}>
                <Button onClick={onClose} variant="outlined" disabled={saving}>
                    Cancelar
                </Button>
                <Button onClick={handleGuardar} variant="contained" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
